import React, { useRef, useState } from 'react';

/**
 * ReferenceUpload
 * ===============
 * Generic, themable single-slot reference uploader used by the
 * BrandAssetsCard. Handles file-pick -> base64 conversion, validation,
 * preview thumbnail, and optional secondary actions (e.g. "Fetch from URL").
 *
 * Props:
 *   label        — slot label (e.g. "Product Image")
 *   helpText     — short helper text under the label
 *   value        — current URL (base64 data, http(s), or /api/... relative)
 *   onChange     — (newValue: string | null) => void
 *   accent       — Tailwind accent class set for this slot
 *                  { ring, border, badge, dot, text }
 *   icon         — JSX icon shown in the empty slot
 *   testIdPrefix — string prepended to data-testid attributes
 *   maxSizeMB    — optional upload cap (default 6 MB)
 *   thumbContain — if true, displays preview as object-contain (logos)
 *   extraAction  — optional JSX rendered under the upload input (e.g. fetch-from-url button)
 */
const DEFAULT_MAX_MB = 6;

function resolveDisplayUrl(value) {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim();
  if (!v) return null;
  if (v.startsWith('data:') || v.startsWith('http://') || v.startsWith('https://')) return v;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${base}${v.startsWith('/') ? '' : '/'}${v}`;
}

export default function ReferenceUpload({
  label,
  helpText,
  value,
  onChange,
  accent,
  icon,
  testIdPrefix,
  maxSizeMB = DEFAULT_MAX_MB,
  thumbContain = false,
  extraAction = null,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    setError('');
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`Image too large — please use a file under ${maxSizeMB} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') onChange(reader.result);
    };
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayUrl = resolveDisplayUrl(value);

  return (
    <div className="flex-1 min-w-[180px]" data-testid={`${testIdPrefix}-slot`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`inline-block w-2 h-2 rounded-full ${accent.dot}`} />
        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          {label}
        </label>
      </div>
      {helpText && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-2 leading-tight">{helpText}</p>
      )}

      {displayUrl ? (
        <div
          className={`flex items-center gap-2 p-2 rounded-xl bg-white/80 dark:bg-slate-800/50 border ${accent.border}`}
          data-testid={`${testIdPrefix}-preview-row`}
        >
          <div className={`w-12 h-12 rounded-lg overflow-hidden shrink-0 border ${accent.border} bg-slate-50 dark:bg-slate-900`}>
            <img
              src={displayUrl}
              alt={label}
              className={`w-full h-full ${thumbContain ? 'object-contain p-1' : 'object-cover'}`}
              loading="lazy"
              data-testid={`${testIdPrefix}-preview-img`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[11px] font-semibold ${accent.text} truncate`}>{label} attached</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
              {value.startsWith('data:') ? 'Uploaded file' : 'Linked URL'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="text-[10px] text-red-500 hover:text-red-600 shrink-0 px-1.5 py-0.5"
            data-testid={`${testIdPrefix}-remove-btn`}
          >
            Remove
          </button>
        </div>
      ) : (
        <label
          onClick={(e) => {
            // Defensive: some browsers (Chrome on Android WebView, older iOS
            // Safari, and pages where a rrweb / analytics extension has
            // intercepted the native label→input binding) don't reliably
            // bubble the click to a `display: none` file input. Manually
            // invoke .click() on the ref as a fallback.
            if (e.target === e.currentTarget || e.target.tagName !== 'INPUT') {
              inputRef.current?.click();
              e.preventDefault();
            }
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`flex flex-col items-center justify-center gap-1 cursor-pointer rounded-xl border-2 border-dashed py-4 px-2 transition-colors ${
            dragOver
              ? `${accent.border} bg-white/90 dark:bg-slate-800/80`
              : 'border-slate-300 dark:border-slate-600 bg-white/40 dark:bg-slate-800/30 hover:bg-white/70 dark:hover:bg-slate-800/50'
          }`}
        >
          <div className={`w-9 h-9 rounded-full ${accent.badge} flex items-center justify-center`}>
            {icon}
          </div>
          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
            Click or drop image
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid={`${testIdPrefix}-upload-input`}
          />
        </label>
      )}

      {extraAction && <div className="mt-2">{extraAction}</div>}

      {error && (
        <p className="mt-1 text-[10px] text-red-500" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
